import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { IconComponent } from '../../elements/icon/icon.component';
import { SortableTableComponent } from '../../elements/sortable-table/sortable-table.component';
import { LoadingComponent } from '../../elements/loading/loading.component';
import { PaginationComponent } from '../../elements/pagination/pagination.component';
import { Icons } from '../../../model/icon';
import { Pagination, PaginationParams } from '../../../model/pagination';
import { Header } from '../../../model/header.model';
import { AirportService } from '../../../services/admin/airport.service';
import { AirportServicesService } from '../../../services/admin/airport-service.service';
import { ServiceService } from '../../../services/admin/service.service';
import { BookingService } from '../../../services/admin/booking.service';
import { LanguageService } from '../../../services/admin/language.service';
import { CountryService } from '../../../services/admin/country.service';
import { CityService } from '../../../services/admin/city.service';
import { AirportService as AirportServiceModel } from '../../../model/airportService';
import { TokenStorageService } from '../../../services/token.storage.service';
import { AdminTableStateService } from '../../../services/admin/admin-table-state.service';
import { MainScreenImageService } from '../../../services/main-screen-image.service';
import { BookingDashboardComponent } from './booking-dashboard/booking-dashboard.component';

type ActiveSection = 'airports' | 'services' | 'bookings' | 'languages' | 'countries' | 'cities' | 'airport-services' | 'main-screen-images';

@Component({
  selector: 'app-admin-page',
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    FormsModule,
    IconComponent,
    SortableTableComponent,
    LoadingComponent,
    PaginationComponent,
    BookingDashboardComponent
  ],
  templateUrl: './admin-page.component.html',
  styleUrls: ['./admin-page.component.scss']
})
export class AdminPageComponent implements OnInit, OnDestroy {
  constructor(
    private router: Router,
    private airportService: AirportService,
    private serviceService: ServiceService,
    private bookingService: BookingService,
    private languageService: LanguageService,
    private countryService: CountryService,
    private cityService: CityService,
    private tokenStorageService: TokenStorageService,
    private airportServicesService: AirportServicesService,
    private adminTableStateService: AdminTableStateService,
    private mainScreenImageService: MainScreenImageService,
  ) {}

  @ViewChild('airportIdInput') airportIdInput!: ElementRef<HTMLInputElement>;

  headers: ActiveSection[] = ['airports', 'services', 'bookings', 'languages', 'countries', 'cities', 'airport-services', 'main-screen-images'];
  secondaryHeaders: ActiveSection[] = ['airports', 'services', 'languages', 'countries', 'cities', 'airport-services', 'main-screen-images'];

  activeSection: ActiveSection = 'bookings';
  bookingView: 'dashboard' | 'table' = 'dashboard';
  settingsOpen: boolean = false;

  loading: boolean = false;

  pagination: Pagination<any> = {
    content: [],
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: true,
    size: 5,
    number: 0,
    numberOfElements: 0,
    empty: true,
    sort: { empty: true, unsorted: true, sorted: false },
    pageable: {
      offset: 0,
      sort: { empty: true, unsorted: true, sorted: false },
      unpaged: false,
      paged: true,
      pageNumber: 0,
      pageSize: 5
    }
  };

  params: PaginationParams = new PaginationParams({ page: 0, size: 5 });

  currentSortHeader: string = '';
  currentSort: string = '';
  getClearSorting: number = 0;

  search: string = '';

  airportServicesData: any[] = [];
  airportServicesLoaded: boolean = false;
  airportIdSearch: string = '';

  tableHeaders: Record<ActiveSection, Header[]> = {
    airports: [
      { title: 'Name', key: 'name', img: false, sortable: true },
      { title: 'IATA', key: 'iata_code', sortable: true },
      { title: 'City', key: 'city', sortable: true },
      { title: 'Country', key: 'country_code', sortable: true },
      { title: '', key: '', edit: true, width: '100px', sortable: false }
    ],
    services: [
      { title: 'ID', key: 'id', sortable: true },
      { title: 'Name', key: 'name', sortable: true },
      { title: 'Description', key: 'description', sortable: false },
      { title: '', key: '', edit: true, width: '100px', sortable: false }
    ],
    bookings: [
      { title: 'ID', key: 'id', sortable: true },
      { title: 'Status', key: 'status', sortable: true },
      { title: 'Trip Type', key: 'tripType', sortable: true },
      { title: 'Flight', key: 'flight.flightNumber', sortable: true },
      { title: 'Date', key: 'flight.date', date: true, sortable: true },
      { title: 'Total', key: 'total', sortable: true },
      { title: '', key: '', edit: true, width: '100px', sortable: false }
    ],
    languages: [
      { title: 'Code', key: 'code639_1', sortable: true },
      { title: 'English Name', key: 'englishName', sortable: true },
      { title: 'Native Name', key: 'nativeName', sortable: true },
      { title: '', key: '', edit: true, width: '100px', sortable: false }
    ],
    countries: [
      { title: 'Code', key: 'code', sortable: true },
      { title: 'Name', key: 'name', sortable: true },
      { title: 'Continent', key: 'continent', sortable: true },
      { title: 'Population', key: 'population', sortable: true },
      { title: '', key: '', edit: true, width: '100px', sortable: false }
    ],
    cities: [
      { title: 'Name', key: 'name', sortable: true },
      { title: 'Code', key: 'city_code', sortable: true },
      { title: 'Country', key: 'country_code', sortable: true },
      { title: 'Population', key: 'population', sortable: true },
      { title: '', key: '', edit: true, width: '100px', sortable: false }
    ],
    'airport-services': [
      { title: 'Airport ID', key: 'airportId', sortable: false },
      { title: 'Service', key: 'serviceName', sortable: false },
      { title: 'Departure (First)', key: 'departurePriceFirst', sortable: false },
      { title: 'Departure (Sub.)', key: 'departurePriceSubsequent', sortable: false },
      { title: 'Arrival (First)', key: 'arrivalPriceFirst', sortable: false },
      { title: 'Arrival (Sub.)', key: 'arrivalPriceSubsequent', sortable: false },
      { title: 'Transit (First)', key: 'transitPriceFirst', sortable: false },
      { title: 'Transit (Sub.)', key: 'transitPriceSubsequent', sortable: false },
      { title: '', key: '', edit: true, width: '100px', sortable: false }
    ],
    'main-screen-images': [
      { title: 'ID', key: 'id', sortable: true },
      { title: 'Image', key: 'url', img: true, sortable: false },
      { title: '', key: '', edit: true, width: '100px', sortable: false }
    ]
  };

  ngOnInit() {
    if (!this.tokenStorageService.isAuthenticated()) {
      this.router.navigate(['/login']);
      return;
    }

    this.restoreTableState();
    this.loadData();

    if (this.activeSection === 'airport-services' && this.airportIdSearch) {
      this.searchAirportServices();
    }
  }

  ngOnDestroy() {}

  loadData() {
    if (this.activeSection === 'bookings' && this.bookingView === 'dashboard') {
      return;
    }

    if (this.activeSection === 'airport-services') {
      this.loading = false;
      return;
    }

    if (this.activeSection === 'main-screen-images') {
      this.loading = true;
      this.mainScreenImageService.getList().subscribe({
        next: (data: any[]) => {
          this.pagination = {
            ...this.pagination,
            content: data,
            totalElements: data.length,
            totalPages: 1,
            number: 0,
            size: data.length > 0 ? data.length : 5,
            numberOfElements: data.length,
            empty: data.length === 0
          };
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.pagination.content = [];
        }
      });
      return;
    }

    this.loading = true;

    const serviceMap: Record<Exclude<ActiveSection, 'airport-services' | 'main-screen-images'>, any> = {
      airports: this.airportService,
      services: this.serviceService,
      bookings: this.bookingService,
      languages: this.languageService,
      countries: this.countryService,
      cities: this.cityService,
    };

    const service = serviceMap[this.activeSection as Exclude<ActiveSection, 'airport-services' | 'main-screen-images'>];

    if (!service) {
      this.loading = false;
      return;
    }

    service.getList(this.params).subscribe({
      next: (data: Pagination<any>) => {
        this.pagination = data;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.pagination.content = [];
      }
    });
  }

  changeActive(tab: ActiveSection) {
    this.settingsOpen = false;

    if (tab === 'bookings') {
      this.bookingView = 'table';
    } else if (this.activeSection === 'bookings' && this.bookingView === 'dashboard') {
      this.bookingView = 'dashboard';
    } else {
      this.bookingView = 'dashboard';
    }

    this.activeSection = tab;
    this.params = new PaginationParams({ page: 0, size: 5 });
    this.search = '';
    this.currentSortHeader = '';
    this.currentSort = '';

    if (tab === 'airport-services') {
      this.airportServicesData = [];
      this.airportServicesLoaded = false;
      this.airportIdSearch = '';
      if (this.airportIdInput) {
        this.airportIdInput.nativeElement.value = '';
      }
    }

    this.saveTableState();
    this.loadData();
  }

  goToDashboard() {
    this.activeSection = 'bookings';
    this.bookingView = 'dashboard';
    this.saveTableState();
  }

  toggleSettings() {
    this.settingsOpen = !this.settingsOpen;
  }

  onOpenItem(id: number | string) {
    if (id === undefined || id === null) return;
    this.router.navigate(['/admin', this.activeSection, id]);
  }

  addNewItem() {
    this.router.navigate(['/admin', this.activeSection, 'new']);
  }

  onSort(sortQuery: string) {
    if (sortQuery && sortQuery.includes(':')) {
      sortQuery = sortQuery.replace(':', ',');
    }
    this.params.sort = sortQuery;
    this.params.page = 0;
    this.saveTableState();
    this.loadData();
  }

  onCurrentSortHeader(header: string) {
    this.currentSortHeader = header;
    this.saveTableState();
  }

  onCurrentSort(sort: string) {
    this.currentSort = sort;
    this.saveTableState();
  }

  onPaginationChange(pagination: Pagination<any>) {
    this.pagination = pagination;
    this.params.page = pagination.number;
    this.params.size = pagination.size;
    this.saveTableState();
    this.loadData();
  }

  searching() {
    if (this.search.length > 2 || this.search.length === 0) {
      this.params.search = this.search;
      this.params.page = 0;
      this.saveTableState();
      this.loadData();
    }
  }

  clearSearch() {
    this.search = '';
    this.params.search = '';
    this.params.page = 0;
    this.getClearSorting++;
    this.saveTableState();
    this.loadData();
  }

  searchAirportServices() {
    const airportId = Number(this.airportIdSearch);
    if (!airportId || isNaN(airportId)) {
      this.airportServicesData = [];
      this.airportServicesLoaded = false;
      this.saveTableState();
      return;
    }

    this.saveTableState();

    this.loading = true;
    this.airportServicesLoaded = false;

    this.airportServicesService.getByAirportId(airportId).subscribe({
      next: (data: AirportServiceModel) => {
        if (data && data.services && data.services.length > 0) {
          this.airportServicesData = data.services.map((service: any) => ({
            airportId: data.airportId,
            serviceName: service.service?.name || 'Unknown',
            departurePriceFirst: service.departurePriceFirst,
            departurePriceSubsequent: service.departurePriceSubsequent,
            arrivalPriceFirst: service.arrivalPriceFirst,
            arrivalPriceSubsequent: service.arrivalPriceSubsequent,
            transitPriceFirst: service.transitPriceFirst,
            transitPriceSubsequent: service.transitPriceSubsequent,
            _raw: service
          }));
        } else {
          this.airportServicesData = [];
        }
        this.airportServicesLoaded = true;
        this.loading = false;
      },
      error: () => {
        this.airportServicesData = [];
        this.airportServicesLoaded = true;
        this.loading = false;
      }
    });
  }

  clearAirportServicesSearch() {
    this.airportIdSearch = '';
    this.airportServicesData = [];
    this.airportServicesLoaded = false;
    this.saveTableState();

    if (this.airportIdInput) {
      this.airportIdInput.nativeElement.value = '';
    }
  }

  private saveTableState(): void {
    this.adminTableStateService.save({
      activeSection: this.activeSection,
      page: this.params.page,
      size: this.params.size,
      sort: this.params.sort ?? '',
      search: this.search,
      currentSortHeader: this.currentSortHeader,
      currentSort: this.currentSort,
      airportIdSearch: this.airportIdSearch
    });
  }

  private restoreTableState(): void {
    const state = this.adminTableStateService.load();

    if (!state) {
      return;
    }

    if (!this.headers.includes(state.activeSection)) {
      return;
    }

    this.activeSection = state.activeSection;
    this.params = new PaginationParams({
      page: state.page ?? 0,
      size: state.size ?? 5,
      sort: state.sort ?? '',
      search: state.search ?? ''
    });

    this.search = state.search ?? '';
    this.currentSortHeader = state.currentSortHeader ?? '';
    this.currentSort = state.currentSort ?? '';
    this.airportIdSearch = state.airportIdSearch ?? '';
  }

  logout() {
    this.tokenStorageService.logout();
    this.router.navigate(['/login']);
  }

  protected readonly Icons = Icons;
}
